import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, NumberField, Typography } from '@heroui/react';
import { getNodeTestIdProp, getNodeTestProps } from '@/utils';
import { formatGel } from '../../sellerMeta';
import type { ShiftFlowValue } from '../useShiftFlow';
import { BREAKDOWN_ROWS, SHIFT_CASH_FORMAT } from '../shiftMeta';
import { ShiftFigureRow } from './ShiftFigureRow';
import { useFocusCountField } from './useFocusCountField';

/**
 * The blind-count step: a full-row GEL cash field the seller fills before seeing
 * what the system expects (the reveal is driven from the modal footer's "confirm
 * count"). After the reveal it shows the Expected / Counted / Difference
 * breakdown, preceded on a close by the server's parts of the expected cash
 * (opening float, cash sales, cash refunds, payouts). A match shows the success
 * line; a discrepancy shows the danger note + re-count, and the footer's "get
 * admin approval" advances to the co-sign step. Render-only — every value comes
 * from `useShiftFlow`.
 *
 * The count field takes focus on arrival (bos-seller#60), and back from
 * "re-count" (SEL-49), and accepts typed digits natively — it's a real
 * `NumberField.Input`, not a numpad-driven buffer like the payment amount.
 * Once the count is confirmed it turns read-only (SEL-48): "re-count" is the
 * only way to change it.
 */
export const ShiftCountStep: React.FC<ShiftFlowValue> = ({
    isModalOpen,
    counted,
    setCounted,
    isCountLocked,
    revealed,
    recount,
    expected,
    breakdown,
    diff,
    diffKind,
    canStart,
    needsApproval,
}) => {
    const { t } = useTranslation('pages');
    const inputRef = useFocusCountField(isModalOpen, isCountLocked);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
                <Typography color="muted" type="body-sm">
                    {t('seller.shift.countLabel')}
                </Typography>
                <NumberField
                    aria-label={t('seller.shift.countLabel')}
                    className="w-full"
                    formatOptions={SHIFT_CASH_FORMAT}
                    isReadOnly={isCountLocked}
                    minValue={0}
                    onChange={(value) =>
                        setCounted(
                            value === undefined || Number.isNaN(value)
                                ? null
                                : value,
                        )
                    }
                    value={counted ?? undefined}
                    variant="secondary"
                >
                    <NumberField.Group>
                        <NumberField.DecrementButton />
                        <NumberField.Input
                            className="w-full tabular-nums"
                            ref={inputRef}
                        />
                        <NumberField.IncrementButton />
                    </NumberField.Group>
                </NumberField>
                <Typography color="muted" type="body-xs">
                    {t('seller.shift.countHint')}
                </Typography>
            </div>

            {revealed ? (
                <div
                    className="flex flex-col gap-2 rounded-xl bg-surface-secondary p-3"
                    {...getNodeTestProps('seller-shift-reveal')}
                >
                    {breakdown ? (
                        <div
                            className="flex flex-col gap-1 pb-2"
                            {...getNodeTestIdProp('seller-shift-breakdown')}
                        >
                            {BREAKDOWN_ROWS.map(({ key, labelKey }) => (
                                <div
                                    className="flex items-center justify-between"
                                    key={key}
                                >
                                    <Typography color="muted" type="body-xs">
                                        {t(labelKey)}
                                    </Typography>
                                    <Typography
                                        className="tabular-nums"
                                        {...getNodeTestIdProp(
                                            `seller-shift-breakdown-${key}`,
                                        )}
                                        type="body-xs"
                                    >
                                        {formatGel(breakdown[key])}
                                    </Typography>
                                </div>
                            ))}
                        </div>
                    ) : null}
                    <ShiftFigureRow
                        label={t('seller.shift.expected')}
                        testId="seller-shift-expected"
                        value={formatGel(expected)}
                    />
                    <ShiftFigureRow
                        label={t('seller.shift.counted')}
                        testId="seller-shift-counted"
                        value={formatGel(counted ?? 0)}
                    />
                    <ShiftFigureRow
                        diffKind={diffKind}
                        label={t('seller.shift.diff')}
                        testId="seller-shift-diff"
                        value={formatGel(diff)}
                    />
                </div>
            ) : null}

            {canStart ? (
                <Typography
                    className="text-success"
                    {...getNodeTestProps('seller-shift-allgood')}
                    type="body-sm"
                >
                    {t('seller.shift.allGood')}
                </Typography>
            ) : null}

            {needsApproval ? (
                <div
                    className="flex flex-col gap-3"
                    {...getNodeTestProps('seller-shift-discrepancy')}
                >
                    <Typography className="text-danger" type="body-sm">
                        {t('seller.shift.discrepancy')}
                    </Typography>
                    <Button
                        className="self-start"
                        {...getNodeTestProps('seller-shift-recount')}
                        onPress={recount}
                        size="sm"
                        variant="outline"
                    >
                        {t('seller.shift.recount')}
                    </Button>
                </div>
            ) : null}
        </div>
    );
};
